import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';

import useRealtimeSocket from './useRealtimeSocket';
import useScopePermissions from './useScopePermissions';
import { useMyMember } from './useRmc2Query';
import { queryKeys } from '../query/queryKeys';

/**
 * Turns RMC2 WebSocket events into cache invalidations and, where the news is
 * for a human rather than a screen, a toast.
 *
 * Mounted once inside the app shell. A member joins their own room and gets told
 * the moment a payment is verified, a commission lands, or a payout is released;
 * an admin joins the shared control room and sees the queues change under them
 * without refreshing. Which of the two you get is decided by scopes, and the
 * server enforces the same split — joining a room you have no business in is
 * refused there, not here.
 *
 * Every handler ends in an invalidation rather than a cache patch: the event
 * says "this changed", the refetch says what it changed to. Writing the payload
 * straight into the cache would make the screen depend on the event carrying
 * every field the list renders, which is the shape-drift trap of Rule 79.
 */
export default function useRmc2Realtime() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { subscribe, subscribeAll } = useRealtimeSocket();
  const { hasAnyScope } = useScopePermissions();

  const isAdmin = hasAnyScope([
    'billing.verify', 'claims.review', 'payouts.release',
    'members.view-all', 'system:users-management',
  ]);

  // Members only — an admin has no member row, and asking for one would 404 on
  // every page load.
  const me = useMyMember(!isAdmin);
  const memberId = me.data?.id ?? null;

  // ── The member's own room ────────────────────────────────────────────────
  useEffect(() => {
    if (!memberId) return undefined;

    return subscribe(`member:${memberId}`, (event) => {
      switch (event.type) {
        case 'PAYMENT_VERIFIED':
          qc.invalidateQueries({ queryKey: queryKeys.billing.all });
          qc.invalidateQueries({ queryKey: queryKeys.members.me() });
          toast.success(t('rmc2.realtime.paymentVerified'));
          break;

        case 'PAYMENT_RETURNED':
          qc.invalidateQueries({ queryKey: queryKeys.billing.all });
          toast.error(event.reason || t('rmc2.realtime.paymentReturned'), { duration: 8000 });
          break;

        case 'COMMISSION_EARNED':
          qc.invalidateQueries({ queryKey: queryKeys.network.all });
          toast.success(t('rmc2.realtime.commissionEarned', {
            amount: event.amount,
            name: event.from_name,
          }));
          break;

        case 'PAYOUT_STATUS_UPDATED':
          qc.invalidateQueries({ queryKey: queryKeys.payouts.all });
          qc.invalidateQueries({ queryKey: queryKeys.network.all });
          if (event.status === 'Released') {
            toast.success(t('rmc2.realtime.payoutReleased', { reference: event.reference_number }), {
              duration: 8000,
            });
          }
          break;

        case 'CLAIM_STATUS_UPDATED':
          qc.invalidateQueries({ queryKey: queryKeys.claims.all });
          toast(t('rmc2.realtime.claimUpdated', {
            id: event.claim_id,
            status: t(`rmc2.status.${event.status}`, { defaultValue: event.status }),
          }));
          break;

        case 'RENEWAL_ALERT':
          qc.invalidateQueries({ queryKey: queryKeys.billing.all });
          qc.invalidateQueries({ queryKey: queryKeys.members.me() });
          break;

        // A resolved ticket stops accepting replies, so a member sitting on the
        // thread needs to know the moment it closes rather than on their next
        // send attempt.
        case 'TICKET_STATUS_UPDATED':
          qc.invalidateQueries({ queryKey: queryKeys.support.all });
          qc.invalidateQueries({ queryKey: queryKeys.support.unread() });
          toast(t('rmc2.realtime.ticketStatusUpdated', {
            id: event.ticket_id,
            status: t(`rmc2.status.${event.status}`, { defaultValue: event.status }),
          }));
          break;

        // SYNC_STATE lands on join; the queries below it are already fresh.
        default:
          break;
      }
    });
  }, [memberId, subscribe, qc, t]);

  // ── The shared admin control room ────────────────────────────────────────
  // Branch scoping happens server-side: a Branch Admin's connection is filtered
  // by branch_id before the event is written to their socket, so nothing here
  // needs to re-check it (and nothing here could be trusted to).
  useEffect(() => {
    if (!isAdmin) return undefined;

    return subscribe('admin:control', (event) => {
      switch (event.type) {
        case 'PROOF_SUBMITTED':
          qc.invalidateQueries({ queryKey: queryKeys.billing.all });
          qc.invalidateQueries({ queryKey: queryKeys.reports.summary() });
          break;

        case 'CLAIM_FILED':
          qc.invalidateQueries({ queryKey: queryKeys.claims.all });
          qc.invalidateQueries({ queryKey: queryKeys.reports.summary() });
          break;

        case 'PAYOUT_REQUESTED':
          qc.invalidateQueries({ queryKey: queryKeys.payouts.all });
          qc.invalidateQueries({ queryKey: queryKeys.reports.summary() });
          break;

        case 'MEMBER_REGISTERED':
          qc.invalidateQueries({ queryKey: queryKeys.members.all });
          qc.invalidateQueries({ queryKey: queryKeys.reports.summary() });
          break;

        case 'EXPIRY_DIGEST':
          qc.invalidateQueries({ queryKey: queryKeys.reports.summary() });
          break;

        // The helpdesk queue is worked from a screen an agent leaves open all
        // day. Without this it only learns about new tickets on a refocus, so
        // an Urgent one can sit unseen for as long as the agent stays put.
        // A member replying to a Resolved ticket puts it back in the queue —
        // an agent watching the board has to see that without a refresh.
        case 'TICKET_REOPENED':
          qc.invalidateQueries({ queryKey: queryKeys.support.all });
          qc.invalidateQueries({ queryKey: queryKeys.support.unread() });
          toast(t('rmc2.realtime.ticketReopened', { id: event.ticket_id }));
          break;

        case 'TICKET_OPENED':
          qc.invalidateQueries({ queryKey: queryKeys.support.all });
          qc.invalidateQueries({ queryKey: queryKeys.support.unread() });

          if (event.priority === 'Urgent') {
            toast(t('rmc2.realtime.urgentTicketOpened', { id: event.ticket_id }), {
              icon: '⚠️',
              duration: 8000,
            });
          }
          break;

        default:
          break;
      }
    });
  }, [isAdmin, subscribe, qc, t]);

  // ── Principal changes ────────────────────────────────────────────────────
  // PRINCIPAL_CHANGED is addressed to the connection, not a room, so it arrives
  // through subscribeAll. A branch reassignment or scope edit changes what this
  // user is allowed to see, which makes EVERY cached list suspect — clear the
  // lot rather than guess which ones moved.
  useEffect(() => subscribeAll((event) => {
    if (event?.type !== 'PRINCIPAL_CHANGED') return;

    qc.clear();
    toast(t('rmc2.realtime.principalChanged'), { duration: 8000 });
  }), [subscribeAll, qc, t]);
}
